import { toIntlLocale } from 'shared/helpers/intlLocale';

// Duracao nos relatorios, em portugues.
//
// O `formatTime` de @chatwoot/utils devolve "0 Sec", "5 Min", "2 Hrs", "1 Day":
// unidade em ingles no meio de uma tela em portugues, e pior, um "0 Sec" que
// finge medicao onde nao houve nenhuma. Aqui as unidades sao os simbolos
// (s, min, h, d), iguais em qualquer idioma, e a ausencia de medida vira o
// marcador de vazio em vez de zero.
//
// Regra do dono: numero sem dado mostra o marcador de vazio, nunca zero.

/** O ponto que o mockup usa no lugar de um numero que nao existe. */
export const EMPTY_METRIC_MARKER = '·';

const SECOND = 1;
const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

const part = (value, unit) => `${value} ${unit}`;

/**
 * Duracao legivel a partir de segundos.
 *
 * Devolve o marcador de vazio quando nao ha medida (null, undefined, NaN, 0 ou
 * negativo): tempo medio de resposta 0 nao e uma resposta instantanea, e a
 * ausencia de conversa respondida no periodo.
 *
 * @param {number} seconds
 * @returns {string}
 */
export const formatReportDuration = seconds => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return EMPTY_METRIC_MARKER;

  if (total < MINUTE) {
    return part(Math.round(total / SECOND), 's');
  }

  if (total < HOUR) {
    const minutes = Math.floor(total / MINUTE);
    const rest = Math.round(total % MINUTE);
    return rest
      ? `${part(minutes, 'min')} ${part(rest, 's')}`
      : part(minutes, 'min');
  }

  if (total < DAY) {
    const hours = Math.floor(total / HOUR);
    const rest = Math.floor((total % HOUR) / MINUTE);
    return rest ? `${part(hours, 'h')} ${part(rest, 'min')}` : part(hours, 'h');
  }

  const days = Math.floor(total / DAY);
  const rest = Math.floor((total % DAY) / HOUR);
  return rest ? `${part(days, 'd')} ${part(rest, 'h')}` : part(days, 'd');
};

/**
 * Contagem legivel. Mesma regra: sem dado mostra o marcador, nunca zero
 * fabricado. Zero MEDIDO (a conta tem o numero e ele e zero) e responsabilidade
 * de quem chama: passe `allowZero` quando o zero for a verdade.
 *
 * @param {number} value
 * @param {{ allowZero?: boolean, locale?: string }} options
 * @returns {string}
 */
export const formatReportCount = (
  value,
  { allowZero = false, locale } = {}
) => {
  if (value === null || value === undefined || value === '') {
    return EMPTY_METRIC_MARKER;
  }
  const total = Number(value);
  if (!Number.isFinite(total)) return EMPTY_METRIC_MARKER;
  if (total === 0 && !allowZero) return EMPTY_METRIC_MARKER;
  // Sem locale, segue o padrão do navegador (undefined), como antes.
  const intlLocale = locale && toIntlLocale(locale);
  return total.toLocaleString(intlLocale);
};

/** Uma serie de grafico so vale a pena desenhar quando tem algum valor. */
export const hasChartData = points =>
  Array.isArray(points) &&
  points.some(
    point => Number(point?.value || 0) !== 0 || Number(point?.count || 0) !== 0
  );
